'use client';
import React, { useRef, forwardRef, useImperativeHandle, ForwardedRef } from 'react';
import { ResponsiveImage, Box, AnimationProps } from '@/components';
import classNames from 'classnames';
import { IResponsiveImageProps } from './ResponsiveImage';
import VideoPlayer, { IVideoPlayerHandle } from './VideoPlayer';
import VimeoPlayer, { IVimeoPlayerHandle } from './VimeoPlayer';

export interface IMediaHandle {
  openFullscreen(): void;
  play(): void;
  pause(): void;
}

export interface IMediaProps extends AnimationProps {
  onAutoPlayStarted?: () => void;
  media: any;
  coverImageProps?: Pick<IResponsiveImageProps, Exclude<keyof IResponsiveImageProps, 'image'>>;
  cover?: boolean;
  controls?: boolean;
  muted?: boolean;
  className?: string;
  ref?: ForwardedRef<HTMLElement>;
}
const Media = forwardRef(function Media(
  {
    media,
    coverImageProps,
    cover,
    controls,
    muted,
    className,
    onAutoPlayStarted,
    animation,
    animate,
    ...props
  }: IMediaProps,
  ref,
) {
  const vimeoPlayer = useRef<IVimeoPlayerHandle>(null);
  const videoPlayer = useRef<IVideoPlayerHandle>(null);

  useImperativeHandle(ref, () => {
    return {
      openFullscreen() {
        if (media.vimeoId) {
          vimeoPlayer?.current?.openFullscreen();
        } else if (media.videoFromGallery) {
          videoPlayer?.current?.openFullscreen();
        }
      },
      play() {
        if (media.vimeoId) {
          vimeoPlayer?.current?.play();
        } else if (media.videoFromGallery) {
          videoPlayer?.current?.play();
        }
      },
      pause() {
        if (media.vimeoId) {
          vimeoPlayer?.current?.pause();
        } else if (media.videoFromGallery) {
          videoPlayer?.current?.pause();
        }
      },
    };
  }, [media]);

  if (!media) {
    return null;
  }

  return (
    <Box
      animation={animation}
      animate={animate || !!animation}
      className={classNames('relative h-full w-full overflow-hidden', className)}
      {...props}
    >
      {media.coverImage && (
        <ResponsiveImage
          image={media.coverImage}
          {...coverImageProps}
          className={classNames('object-cover', coverImageProps?.className)}
        />
      )}
      {(media.videoFromGallery || media.vimeoId || media.youtubeId) &&
        (media.vimeoId ? (
          <VimeoPlayer
            ref={vimeoPlayer}
            media={media}
            cover={cover}
            controls={controls}
            onAutoPlayStarted={onAutoPlayStarted}
          />
        ) : media.videoFromGallery ? (
          <VideoPlayer
            ref={videoPlayer}
            media={media}
            cover={cover}
            controls={controls}
            onAutoPlayStarted={onAutoPlayStarted}
            muted={muted}
          />
        ) : null)}
    </Box>
  );
});

export default Media;
